import { NavLink } from 'react-router-dom';
import { useCachedFetch } from '../lib/use-cached-fetch';

// The shared scoreboard ribbon — a thin, always-on strip of canonical
// company numbers above the rail (the role-redesign "alignment layer", see
// docs/redesign/role-based-redesign-plan.html). It is a LAYER, not a tab:
// the same figures every role sees, so no two homes ever disagree.
//
// P0: condensed REUSE of the existing, ungated, 30s-cached /api/pulse
// endpoint — no new data, no new gate. Shows week-to-date (`wtd`) inflow for
// the canonical funnel metrics. The full /pulse page sits behind it.

interface CounterView {
  wtd: number;
  lastWeekPace: number;
}
interface MoneyCounter {
  wtdUsd: number;
  lastWeekPaceUsd: number;
}
interface PulseResponse {
  metrics: {
    signups: CounterView;
    activated: CounterView;
    pqls: CounterView;
    payments: CounterView;
  };
  revenue: MoneyCounter;
  scoreboard?: {
    customersCumulative: number | null;
    bookedArrMinor: number | null;
    coverageMultiple: number | null;
  };
}

function usd(v: number): string {
  if (v >= 1000) return `$${(v / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// vs-prior-week pace delta, floor-gated so tiny weeks don't scream.
function delta(now: number, pace: number): { txt: string; up: boolean } | null {
  if (!pace || pace < 3) return null;
  const pct = Math.round(((now - pace) / pace) * 100);
  if (pct === 0) return null;
  return { txt: `${pct > 0 ? '▲' : '▼'}${Math.abs(pct)}%`, up: pct > 0 };
}

export default function ScoreboardRibbon() {
  const { data } = useCachedFetch<PulseResponse>('/api/pulse');

  // The canonical five the plan defines (Signups · Activated · Customers · ARR ·
  // Coverage) — the same numbers every role's Home shows. Signups/Activated are
  // week-to-date inflow (with a pace delta); Customers/ARR are cumulative totals;
  // Coverage shows the spend-payback multiple with a good/great verdict word.
  const sb = data?.scoreboard;
  const cov = sb?.coverageMultiple ?? null;
  const covVerdict: { txt: string; up: boolean } | null =
    cov == null ? null : cov >= 5 ? { txt: 'great', up: true } : cov >= 3 ? { txt: 'good', up: true } : { txt: 'low', up: false };
  const money = (minor: number | null | undefined) => (minor == null ? '—' : usd(minor / 100));
  const tiles: Array<{ k: string; v: string; d: { txt: string; up: boolean } | null }> = data
    ? [
        { k: 'Signups', v: String(data.metrics.signups.wtd), d: delta(data.metrics.signups.wtd, data.metrics.signups.lastWeekPace) },
        { k: 'Activated', v: String(data.metrics.activated.wtd), d: delta(data.metrics.activated.wtd, data.metrics.activated.lastWeekPace) },
        { k: 'Customers', v: sb?.customersCumulative != null ? String(sb.customersCumulative) : '—', d: null },
        { k: 'ARR', v: money(sb?.bookedArrMinor), d: null },
        { k: 'Coverage', v: cov != null ? `${cov.toFixed(1)}×` : '—', d: covVerdict },
      ]
    : [
        { k: 'Signups', v: '—', d: null },
        { k: 'Activated', v: '—', d: null },
        { k: 'Customers', v: '—', d: null },
        { k: 'ARR', v: '—', d: null },
        { k: 'Coverage', v: '—', d: null },
      ];

  return (
    <NavLink to="/pulse" className="ribbon" title="This week so far · open the full Pulse scoreboard">
      {tiles.map((t) => (
        <span className="ribbon__tile" key={t.k}>
          <span className="ribbon__k">{t.k}</span>
          <span className="ribbon__v">{t.v}</span>
          {t.d && <span className={`ribbon__d ${t.d.up ? 'up' : 'down'}`}>{t.d.txt}</span>}
        </span>
      ))}
      <span className="ribbon__end">shared layer · same number for everyone →</span>
    </NavLink>
  );
}
